import { CobMetaPipe } from '@lockton-apps/shared/ui/pipes';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { fetch } from '@ngrx/router-store/data-persistence';
import { Store } from '@ngrx/store';
import { defaults, isUndefined, map as _map, toInteger } from 'lodash';
import {
  catchError,
  filter,
  first,
  map,
  merge,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import {
  ClientContactsActions,
  ClientPoliciesActions,
} from '@lockton-apps/clients/actions';
import { ContactCreateUpdateActions } from '@lockton-apps/contacts/action';
import {
  DashboardAdminActions,
  DashboardBrokerActions,
  DashboardBrokerLsosActions,
  DashboardBrokerNorwayActions,
  DashboardContactActions,
} from '@lockton-apps/dashboard/action';
import { EnquiryOverviewActions } from '@lockton-apps/enquiries/action';
import {
  PolicyCancellationActions,
  PolicyCreateUpdateActions,
  PolicyDetailsActions,
} from '@lockton-apps/policies/action';
import { AuthApiActions } from '@lockton-apps/shared/action/auth';
import { ClientApiActions } from '@lockton-apps/shared/action/clients';
import {
  PoliciesApiActions,
  PoliciesApiActionTypes,
} from '@lockton-apps/shared/action/policies';
import { QuotesApiActions } from '@lockton-apps/shared/action/quotes';
import { AuthSelectors } from '@lockton-apps/shared/data-access/auth';
import { PortalConfigSelectors } from '@lockton-apps/shared/data-access/portal-config';
import {
  ClientRouterSelectors,
  PolicyRouterSelectors,
} from '@lockton-apps/shared/data-access/router';
import { Partners } from '@lockton-apps/shared/interfaces';
import {
  ConfirmationDialogComponent,
  ConfirmDialogData,
  FeedbackService,
} from '@lockton-apps/shared/ui/lui';
import { getProposalUrl } from '@lockton-apps/shared/util/proposal-utils';

import { PoliciesSelectors } from '../..';
import { PoliciesService } from '../services/policies.service';
import { PoliciesStateActions } from './policies.actions';
import { selectEntities } from './policies.selectors';
import { DateFnsHelpersService } from '@lockton-apps/shared/util/ngx-date-fns-tz';
import { isExperimental } from '@lockton-apps/shared/util/helpers';

const MESSAGES: Record<
  string,
  {
    text: string;
    type: 'info' | 'success' | 'error';
  }
> = {
  [PoliciesApiActionTypes.AddedSuccess]: {
    text: $localize`Policy created successfully`,
    type: 'success',
  },
  [PoliciesApiActionTypes.AddedFailure]: {
    text: $localize`Unable to create policy at this time, please try again later`,
    type: 'error',
  },
  [PoliciesApiActionTypes.UpdatedSuccess]: {
    text: $localize`Policy updated successfully`,
    type: 'success',
  },
  [PoliciesApiActionTypes.UpdatedFailure]: {
    text: $localize`Unable to update policy at this time, please try again later`,
    type: 'error',
  },
  [PoliciesApiActionTypes.DeletedSuccess]: {
    text: $localize`Policy deleted successfully`,
    type: 'success',
  },
  [PoliciesApiActionTypes.DeletedFailure]: {
    text: $localize`Unable to delete policy at this time, please try again later`,
    type: 'error',
  },
  [PoliciesApiActionTypes.RestoredSuccess]: {
    text: $localize`Policy restored successfully`,
    type: 'success',
  },
  [PoliciesApiActionTypes.RestoredFailure]: {
    text: $localize`Unable to restore policy at this time, please try again later`,
    type: 'error',
  },
};

@Injectable()
export class PoliciesEffects {
  /** Used for takeuntils  */
  private navigated$ = this.actions$.pipe(ofType(ROUTER_NAVIGATED));

  loadPolicies$ = createEffect(() =>
    merge(
      this.actions$.pipe(ofType(DashboardContactActions.init)),
      this.actions$.pipe(
        ofType(
          DashboardBrokerLsosActions.init,
          DashboardBrokerNorwayActions.init,
          DashboardBrokerActions.init,
          DashboardContactActions.init,
        ),
        filter(() => !isExperimental()),
      ),
      this.actions$.pipe(
        ofType(ContactCreateUpdateActions.init),
        concatLatestFrom(() =>
          this.store.select(AuthSelectors.selectIsContact),
        ),
        filter(([, isContact]) => isContact),
      ),
    ).pipe(
      switchMap(() =>
        this.policiesService.all().pipe(
          map((policies) =>
            PoliciesApiActions.loadPoliciesSuccess({
              policies: policies || [],
            }),
          ),
          catchError((error) =>
            of(PoliciesApiActions.loadPoliciesFailure({ error })),
          ),
        ),
      ),
    ),
  );

  loadRecentlyViewedPolicies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardAdminActions.init),
      fetch({
        run: () => {
          return this.policiesService.recentlyViewed().pipe(
            map((res) =>
              PoliciesApiActions.loadRecentlyViewedPoliciesSuccess({
                policies: res || [],
              }),
            ),
          );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return PoliciesApiActions.loadRecentlyViewedPoliciesFailure({
            error,
          });
        },
      }),
    ),
  );

  clientPoliciesFeatureInit$ = this.actions$.pipe(
    ofType(ClientPoliciesActions.init),
    concatLatestFrom(() =>
      this.store
        .select(ClientRouterSelectors.selectRoutedId)
        .pipe(filter(Boolean)),
    ),
    map(([, clientId]) => clientId),
  );

  enquiriesFeatureInitAndQuoteLoaded$ = this.actions$.pipe(
    ofType(EnquiryOverviewActions.init),
    switchMap(() =>
      this.actions$.pipe(
        ofType(QuotesApiActions.loadQuoteSuccess),
        takeUntil(this.navigated$),
      ),
    ),
    map(({ quote }) => quote.clientId),
  );

  loadClientPolicies$ = createEffect(() =>
    merge(
      this.clientPoliciesFeatureInit$,
      this.enquiriesFeatureInitAndQuoteLoaded$,
    ).pipe(
      switchMap((clientId) => {
        if (!clientId) {
          throw new Error('No client Id');
        }

        return this.policiesService.allForClient(clientId).pipe(
          map((policies) =>
            PoliciesApiActions.loadClientPoliciesSuccess({ policies }),
          ),
          catchError((error) =>
            of(PoliciesApiActions.loadClientPoliciesFailure({ error })),
          ),
        );
      }),
    ),
  );

  loadClientsPolicies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ContactCreateUpdateActions.init),
      switchMap(() =>
        this.actions$.pipe(
          ofType(ClientApiActions.loadClientAndSubClientsSuccess),
          first(),
          map(({ clients }) => _map(clients, (client) => client.id)),
        ),
      ),
      concatLatestFrom(() => this.store.select(AuthSelectors.selectIsContact)),
      filter(([, isContact]) => !isContact),
      map(([clientIds]) => clientIds),
      switchMap((clientIds) =>
        this.policiesService
          .allForClients(clientIds)
          .pipe(
            map((policies) =>
              PoliciesApiActions.loadClientsPoliciesSuccess({ policies }),
            ),
          ),
      ),
    ),
  );

  loadSeveral$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientContactsActions.contactSelected),
      switchMap(({ contact }) =>
        this.policiesService.loadSeveral(contact.policyIds).pipe(
          map((policies) =>
            PoliciesApiActions.loadSeveralSuccess({ policies }),
          ),
          catchError((error) =>
            of(PoliciesApiActions.loadSeveralFailure({ error })),
          ),
        ),
      ),
    ),
  );

  loadPolicyForPolicyDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyDetailsActions.init),
      concatLatestFrom(() =>
        this.store.select(PolicyRouterSelectors.selectRoutedId),
      ),
      switchMap(([, policyId]) => {
        if (!policyId) {
          throw new Error('No policy Id');
        }
        return this.policiesService.load(policyId).pipe(
          map((policy) =>
            PoliciesApiActions.loadPolicyForPolicyDetailsSuccess({ policy }),
          ),
          catchError((error) =>
            of(PoliciesApiActions.loadPolicyFailure({ error })),
          ),
        );
      }),
    ),
  );

  loadPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyCreateUpdateActions.init, PolicyCancellationActions.init),
      concatLatestFrom(() =>
        this.store.select(PolicyRouterSelectors.selectRoutedId),
      ),
      filter(([, policyId]) => !isUndefined(policyId)),
      switchMap(([, policyId]) => {
        if (!policyId) {
          throw new Error('No policy Id');
        }
        return this.policiesService.load(policyId).pipe(
          map((policy) =>
            PoliciesApiActions.loadPolicySuccess({
              policy,
            }),
          ),
          catchError((error) =>
            of(PoliciesApiActions.loadPolicyFailure({ error })),
          ),
        );
      }),
    ),
  );

  // Shared Policies - To Refactor

  loadAllPolicies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.contactAuthenticated),
      switchMap(() =>
        this.policiesService.all().pipe(
          map((policies) =>
            PoliciesApiActions.loadPoliciesSuccess({
              policies: policies || [],
            }),
          ),
          catchError((error) =>
            of(PoliciesApiActions.loadPoliciesFailure({ error })),
          ),
        ),
      ),
    ),
  );

  add$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyCreateUpdateActions.added),
      concatLatestFrom(() =>
        this.store.select(ClientRouterSelectors.selectQueriedId),
      ),
      map(([{ policy }, clientId]) => {
        return defaults(
          {
            clientId: toInteger(clientId),
            classOfBusinessId: toInteger(policy.classOfBusinessId),
            inceptionDate: this.dateFnsHelperService.toNeutralDate(
              policy.inceptionDate,
            ),
            expiryDate: this.dateFnsHelperService.toNeutralDate(
              policy.expiryDate,
            ),
            renewalDate: this.dateFnsHelperService.toNeutralDate(
              policy.renewalDate,
            ),
          },
          policy,
        );
      }),
      switchMap((policy) =>
        this.policiesService.create(policy).pipe(
          map((res) => PoliciesApiActions.addedSuccess({ policy: res })),
          catchError((error) => of(PoliciesApiActions.addedFailure({ error }))),
        ),
      ),
    ),
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyCreateUpdateActions.updated),
      concatLatestFrom(() =>
        this.store
          .select(PoliciesSelectors.selectRoutedPolicy)
          .pipe(filter(Boolean)),
      ),
      map(([{ policy }, existingPolicy]) =>
        defaults(
          {
            id: existingPolicy.id,
            classOfBusinessId: toInteger(policy.classOfBusinessId),
            clientId: existingPolicy.policyClients[0].id,
            inceptionDate: this.dateFnsHelperService.toNeutralDate(
              policy.inceptionDate,
            ),
            expiryDate: this.dateFnsHelperService.toNeutralDate(
              policy.expiryDate,
            ),
            renewalDate: this.dateFnsHelperService.toNeutralDate(
              policy.renewalDate,
            ),
          },
          policy,
        ),
      ),
      switchMap((policy) =>
        this.policiesService.update(policy).pipe(
          map((res) => PoliciesApiActions.updatedSuccess({ policy: res })),
          catchError((error) =>
            of(PoliciesApiActions.updatedFailure({ error })),
          ),
        ),
      ),
    ),
  );

  deleteDialogConfirmed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyDetailsActions.deleted),
      concatLatestFrom(() =>
        this.store
          .select(PoliciesSelectors.selectRoutedPolicy)
          .pipe(filter(Boolean)),
      ),
      switchMap(([{ id }, policy]) =>
        this.dialog
          .open<ConfirmationDialogComponent, ConfirmDialogData, boolean>(
            ConfirmationDialogComponent,
            {
              data: {
                title: $localize`Are you sure?`,
                content: $localize`You are about to delete the policy <span class="fw-b">${
                  this.cobMetaPipe.transform(policy.classOfBusinessId).title
                }</span> for <span class="fw-b">${
                  policy.policyClients[0].insuredName
                }</span>. Are you sure you want to continue? <i class="mat-small"><strong class="d-block mt-2 pt-2 border-top">Important note:</strong> The associated documents folder, along with contents will be deleted along with the policy. The documents to be retained should be downloaded before proceeding.</i>`,
                confirmButtonText: $localize`Delete`,
                cancelButtonText: $localize`Cancel`,
              },
            },
          )
          .afterClosed()
          .pipe(
            filter(Boolean),
            map(() => PoliciesStateActions.deleteConfirmation({ id })),
          ),
      ),
    ),
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PoliciesStateActions.deleteConfirmation),
      switchMap(({ id }) =>
        this.policiesService.delete(id).pipe(
          map(() => PoliciesApiActions.deletedSuccess({ id })),
          catchError((error) =>
            of(PoliciesApiActions.deletedFailure({ id, error })),
          ),
        ),
      ),
    ),
  );

  restore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyDetailsActions.restored),
      switchMap(({ id }) =>
        this.policiesService.restore(id).pipe(
          map(() => PoliciesApiActions.restoredSuccess({ id })),
          catchError((error) =>
            of(PoliciesApiActions.restoredFailure({ id, error })),
          ),
        ),
      ),
    ),
  );

  mta$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PolicyDetailsActions.editPolicyClicked),
      switchMap(({ policyId }) =>
        this.dialog
          .open<ConfirmationDialogComponent, ConfirmDialogData, boolean>(
            ConfirmationDialogComponent,
            {
              data: {
                title: $localize`Before you proceed`,
                content: $localize`In order to make a change to your policy you must first make the relevant changes and submit the proposal form. On completing the proposal form you will be presented with a revised premium or notified if there is no increase in premium.<br><br>If you would like to proceed please <span class="fw-b">'Continue'</span> below.`,
                confirmButtonText: $localize`Continue`,
                cancelButtonText: $localize`Cancel`,
              },
            },
          )
          .afterClosed()
          .pipe(
            filter(Boolean),
            switchMap(() =>
              this.policiesService.mta(policyId).pipe(
                map((res) => PoliciesApiActions.mtaSuccess({ proposal: res })),
                catchError((error) =>
                  of(PoliciesApiActions.mtaFailure({ error })),
                ),
              ),
            ),
          ),
      ),
    ),
  );

  // Navigation / Redirects

  navigateToClientPolicies$ = createEffect(
    () =>
      merge(
        this.actions$.pipe(
          ofType(PoliciesApiActions.addedSuccess),
          map(({ policy }) => policy.policyClients[0].id),
        ),
        this.actions$.pipe(
          ofType(PoliciesApiActions.deletedSuccess),
          concatLatestFrom(() => this.store.select(selectEntities)),
          map(
            ([{ id }, policiesDict]) => policiesDict[id]?.policyClients[0].id,
          ),
        ),
      ).pipe(
        tap((clientId) => {
          if (!clientId) {
            return this.router.navigate(['/']);
          }
          return this.router.navigate(['/clients', clientId, 'policies']);
        }),
      ),
    { dispatch: false },
  );

  navigateToPolicyDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PoliciesApiActions.updatedSuccess),
        tap(({ policy }) => this.router.navigate(['/policies', policy.id])),
      ),
    { dispatch: false },
  );

  navigateToProposal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PoliciesApiActions.mtaSuccess),
        tap(({ proposal }) => this.router.navigate(getProposalUrl(proposal))),
      ),
    { dispatch: false },
  );

  // Messages

  feedbackMessages$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PoliciesApiActions.addedSuccess,
          PoliciesApiActions.addedFailure,
          PoliciesApiActions.updatedSuccess,
          PoliciesApiActions.updatedFailure,
          PoliciesApiActions.deletedSuccess,
          PoliciesApiActions.deletedFailure,
          PoliciesApiActions.restoredSuccess,
          PoliciesApiActions.restoredFailure,
        ),
        tap(({ type: actionType }) => {
          const { text, type } = MESSAGES[actionType];

          this.feedbackService.subtleMessage(text, {
            type,
          });
        }),
      ),
    { dispatch: false },
  );

  // Deprecated Effects

  /*** @deprecated for legacy lsos use only */
  dashboardContactLoadPolicies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DashboardContactActions.init),
      concatLatestFrom(() =>
        this.store.select(PortalConfigSelectors.selectActivePortalId),
      ),
      filter(([, activePortalId]) => activePortalId === Partners.LSOS),
      switchMap(() =>
        this.policiesService.all().pipe(
          map((policies) =>
            PoliciesApiActions.loadPoliceiesForContactDashboardSuccess({
              policies: policies || [],
            }),
          ),
          catchError((error) =>
            of(PoliciesApiActions.loadPoliciesFailure({ error })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private policiesService: PoliciesService,
    private router: Router,
    private feedbackService: FeedbackService,
    private dialog: MatDialog,
    private cobMetaPipe: CobMetaPipe,
    private dateFnsHelperService: DateFnsHelpersService,
  ) {}
}
